import { electricalSchematicV1, electricalSchematicV2 } from "./mock/electrical-schematic";
import {
  oldSmartObject,
  sameSmartObjectDifferentBackground,
  sameVariablesDifferentValidValues,
} from "./mock/equipment-cooling-schematic";
import { flapPositionIndicatorV1, flapPositionIndicatorV2 } from "./mock/flap-position-indicator";
import { AnimatedMetaVariable, SmartObjectHotspot } from "./store";
import { getHotspotFreezeTimelineKey, getSmartObjectFontKey, normalizeFontName } from "./utils";

type SVGElements = "svg" | "rect" | "path" | string;

export enum SVG_SMART_OBJECT_TYPES {
  IMAGE_SWAP = "IMAGE_SWAP",
  SWAP_VALUE = "SWAP_VALUE",
  HOTSPOT = "HOTSPOT",
  HOTSPOT_ONCLICK = "HOTSPOT_ONCLICK",
  ROTATE = "ROTATE",
  ROTATE_REF = "ROTATE_REF",
  FILL = "FILL",
  FILL_VALUE = "FILL_VALUE",
  DYNAMIC_TEXT = "DYNAMIC_TEXT",
  DYNAMIC_TEXT_CONFIG = "DYNAMIC_TEXT_CONFIG",
  IMAGE_TEXT = "IMAGE_TEXT",
  FONT = "FONT",
}

export enum SVG_SMART_OBJECT_HOTSPOTS_ACTIONS {
  SET_LITERAL = "SET_LITERAL",
  TOGGLE_NEXT = "SET_INC",
  TOGGLE_PREVIOUS = "SET_DEC",
  SET_MOMENTARY = "SET_MOMENT", // (PUSH) / HOLD
  /** To be defined later  */
  INCREMENT = "INCREMENT",
  DECREMENT = "DECREMENT",
  HOLD_INCREMENT = "HOLD_INCREMENT",
  HOLD_DECREMENT = "HOLD_DECREMENT",
}

interface SvgJson {
  elementName: SVGElements;
  attributes: Record<string, string>;
  children: SvgJson[];
  textContent?: string;
}

export interface SmartObjectElement {
  type?: SVGElements;
  id?: string;
  children?: SmartObjectElement[];
  choices?: SmartObjectElement[];
  allowedValues?: string[];
  GROUP_ID?: string;
  ACTION?: string;
  HOTSPOT_ID?: string;
  VALUE?: string;
  TYPE?: string;
  FONT?: string;
  OPTION_JUSTIFY?: string;
  OPTION_STACK?: string;
  SET_ID?: string;
  RATE?: number;
  groupId?: string;
  value?: string;
  parentId?: string;
  styles?: string;
  style?: string;
  textContent?: string;
  "xlink:href"?: string;
}

export interface SmartObjectDynamicTextLabel {
  transform: string;
}

export interface SmartObjectDynamicTextBoundary {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface SmartObjectDynamicText
  extends SmartObjectElement,
    SmartObjectDynamicTextLabel,
    SmartObjectDynamicTextBoundary {
  hasBoundary: boolean;
  children?: SmartObjectDynamicText[];
}

export function jsonFromSvgString(svgString: string): SvgJson {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svgElement = doc.documentElement;
  return jsonFromSvgElement(svgElement);
}

export function jsonFromSvgElement(svgEl: Element): SvgJson {
  const jsonSvg: SvgJson = {
    elementName: svgEl.tagName,
    attributes: {},
    children: [],
  };
  for (const attribute of svgEl.attributes) {
    jsonSvg.attributes[attribute.name] = attribute.value;
  }
  for (const child of svgEl.children) {
    jsonSvg.children.push(jsonFromSvgElement(child));
  }

  if (svgEl.textContent) {
    jsonSvg.textContent = svgEl.textContent;
  }

  return jsonSvg;
}

const keywords = [
  /**
   * should be one of SVG_SMART_OBJECT_HOTSPOTS_ACTIONS
   */
  "ACTION",
  /**
   * is the GROUP_ID of the affected element
   */
  "SET_ID",
  /**
   * should be one of SVG_SMART_OBJECT_TYPES
   */
  "TYPE",
  /**
   * the id of the meta variable for an IMAGE_SWAP
   */
  "GROUP_ID",
  /**
   * The name that will be displayed on the right advanced panel
   * in the lesson designer
   */
  "NAME",
  /**
   * For hotspots the value that will be set on the group id that it targets
   * for SWAP_VALUE is the value that if its match it will render the image
   */
  "VALUE",
  /**
   * Custom orders for image swap, useful for hot spots and animations
   * to guarantee the correct order of functions like toggleNext and togglePrevious
   */
  "ORDER",
  /**
   * Boolean to have the element displayed or not by default
   */
  "DISPLAY",
  /**
   * if an IMAGE_SWAP has this attribute it will change the controls on the advanced panel
   * to select wether the group is animated or not
   */
  "ANIMATE",
  "ANIMATE_VALUE",
  "HIDE_VALUE",
  /**
   * Rate of the animation of an IMAGE_SWAP in milliseconds
   */
  "RATE",
  /**
   * For hotspots, it sets if the hotspot is enabled or not by default
   */
  "ENABLED",
  /**
   * For TYPE=ROTATE it determines the position of the element to rotate relative to a value
   * should have the following syntax:
   * [TYPE=ROTATE,ANGLES=0:0|40:-120|80:-240]
   * Where the `|` is used as a separator of pair values.
   * The left number of the `:` denotes the "value" and the right number of the `:` denotes
   * the degree of rotation.
   */
  "ANGLES",
  /**
   * Value of a child from TYPE=FILL
   */
  "FILL_VALUE",
  /**
   * Defines a font collection, for now only digits
   * the value should be a `FONT_NAME` thats found in a
   * collection TYPE=FONT
   */
  "FONT",
  /**
   * Defines a name for the font be referenced by another smart element
   * Can also be used with DYNAMIC_TEXT_CONFIG to specify the font of the dynamic text, this value can't be changed by the user.
   */
  "FONT_NAME",
  /**
   * Value of the element Ex: FONT_CHAR=1
   */
  "FONT_CHAR",
  /**
   * Defines how to stack extra characters in the font
   */
  "OPTION_STACK",
  /**
   * Defines how to justify the text it should be either left or right
   */
  "OPTION_JUSTIFY",
  /**
   * This element should have the different configurations for the dynamic text
   * Ex. SIZE / BOLD / ITALIC / UNDERLINE / STROKE / COLOR / etc
   */
  "DYNAMIC_TEXT_CONFIG",
  /**
   * Size of the text, used in conjunction with DYNAMIC_TEXT_CONFIG
   */
  "SIZE",
  /**
   * Index of the property input in the advanced panel
   * This is used to determine the order of the properties in the advanced panel
   */
  "CONTROL_INDEX",
] as const;

/**
 * These properties are allowed to have multiple values that will be combined into an array
 * other properties not in this list will have the last value of the property applied only.
 */
const ARRAY_KEYS_WHITELIST = ["FONT"];

export function parseSvgJsonIntoSmartObjectStructure(svgJson: SvgJson, styles?: string[]) {
  /**
   * This counter is used to add style tags at the top of the SVG, usually within a <defs></defs> tag
   * It is implemented using a counter just in case more than one Style tag is included in the SVG
   * (with all the testing done, usually only one is included) so this might not be needed
   */
  let stylesCounter = 0;

  /**
   * This functions parses the `data-name` attribute that comes
   * in the svg from adobe illustrator, when adding names to the layers of the svg
   * that name gets inserted into both the ID and data-name, we're using that functionality
   * to insert metadata to parse and manipulate the SVG in the lesson editor
   */
  let hotspotCounter = 0;
  const parseDataName = (
    dataName: string,
    parentGroupId?: string | undefined,
    parentName?: string | undefined,
    fontAttributes?: FontAttributes,
  ) => {
    const attributes: { [keY: string]: string | string[] } = {};
    /**
     * This regex match will prepare the data-attribute name to be parsed
     * by the regex further below, it will remove the brackets and the content inside of them
     * Ex: `RandomText [TYPE=IMAGE_SWAP, GROUP_ID=SUPPLY_LEVER] RandomText2`
     * will be converted into `TYPE=IMAGE_SWAP, GROUP_ID=SUPPLY_LEVER`
     */
    const name =
      dataName
        .match(/\[.*\]/)?.[0]
        ?.replaceAll("[", "")
        .replaceAll("]", "") ?? "";

    const regex = new RegExp(`(?:${keywords.join("|")})=([^,]+)`, "g");
    /**
     * This regex will parse the `data-name` attribute from the tags of the svg
     * it will look for a combination of one of the keywords listed above followed by an equals sign
     * and the value should end with a comma
     * Ex: [TYPE=HOTSPOT,ACTION=SET_LITERAL,SET_ID=SUPPLY_LEVER,VALUE=DOWN,DISPLAY=TRUE,ENABLED=TRUE]
     * That string will be converted into
     * {
     * "TYPE": "HOTSPOT",
     * "SET_ID": "SUPPLY_LEVER",
     * "VALUE": "UP",
     * "DISPLAY": "TRUE",
     * "ENABLED": "TRUE"
     * }
     */
    let match;

    while ((match = regex.exec(name)) !== null) {
      const [found, value] = match;
      const [key] = found.split("=");

      if (attributes[key] && ARRAY_KEYS_WHITELIST.includes(key)) {
        attributes[key] = [attributes[key]].flat().concat(value);
      } else {
        attributes[key] = value;
      }
    }

    if (Object.keys(attributes).length > 0) {
      if (parentGroupId) {
        attributes["GROUP_ID"] = attributes["GROUP_ID"] ?? parentGroupId;
        // this is for hotspots
        attributes["SET_ID"] = attributes["SET_ID"] ?? parentGroupId;
      }

      if (parentName) {
        attributes["NAME"] = attributes["NAME"] ?? parentName;
      }

      if (attributes["TYPE"] === "HOTSPOT") {
        const hotSpotName = attributes["NAME"] ?? ++hotspotCounter;
        attributes["HOTSPOT_ID"] = `${attributes["SET_ID"]}-hotspot-${hotSpotName}`;
      }

      // We don't want to inherit or apply this normalization to "IMAGE_TEXT"
      if (fontAttributes?.font && attributes["TYPE"] === "DYNAMIC_TEXT") {
        const fontName = attributes["FONT"] ?? fontAttributes.font;
        attributes["FONT"] = normalizeFontName(fontName);
      }

      if (fontAttributes?.size) {
        attributes["SIZE"] = attributes["SIZE"] ?? fontAttributes.size;
      }
    }

    return attributes;
  };

  type FontAttributes = {
    font?: string | string[];
    size?: string | string[];
    /**
     * Later other options like bold, italic, underline, etc
     * will be added here
     */
  };

  const createElement = (
    json: SvgJson,
    parentGroupId?: string,
    parentName?: string,
    fontAttributes?: FontAttributes,
  ): SmartObjectElement => {
    const { elementName, attributes, children, textContent } = json;
    let parsedDataName;
    let groupId = parentGroupId;
    let name = parentName;
    const fontSettings: FontAttributes = fontAttributes ?? {};

    if (attributes["data-name"]) {
      parsedDataName = parseDataName(attributes["data-name"], parentGroupId, name, fontSettings);
      if (parsedDataName.GROUP_ID) {
        groupId = parsedDataName.GROUP_ID as string;
      }

      if (parsedDataName.NAME) {
        name = parsedDataName.NAME as string;
      }

      // We only should inherit the font from the parent if the element is a DYNAMIC_TEXT
      // "IMAGE_TEXT" also uses the FONT property, but inheritance shouldn't be applied to it
      if (
        parsedDataName.FONT &&
        (parsedDataName.TYPE === "DYNAMIC_TEXT" || parsedDataName.TYPE === "DYNAMIC_TEXT_CONFIG")
      ) {
        fontSettings.font = parsedDataName.FONT;
      }

      if (parsedDataName.SIZE) {
        fontSettings.size = parsedDataName.SIZE;
      }
    }

    switch (elementName) {
      case "svg":
        return {
          type: "svg",
          id: attributes.id,
          children: children.map((child) => createElement(child)),
          ...attributes,
          ...parsedDataName,
        };
      case "g": {
        return {
          type: "g",
          id: attributes.id,
          children: children.map((child) => createElement(child, groupId, name, fontSettings)),
          ...attributes,
          ...parsedDataName,
        };
      }
      case "path": {
        return {
          ...attributes,
          type: "path",
          id: attributes.id,
          children: children.map((child) => createElement(child, groupId, name)),
          ...parsedDataName,
        };
      }
      case "image": {
        return {
          ...attributes,
          id: attributes.id,
          type: "image",
          groupId: parentGroupId ?? undefined,
          ...parsedDataName,
        };
      }
      case "use": {
        return {
          ...attributes,
          id: attributes.id,
          type: "use",
          ...parsedDataName,
        };
      }
      case "style": {
        const style = !!styles && styles[stylesCounter] ? styles[stylesCounter] : undefined;
        stylesCounter++;

        return {
          ...attributes,
          id: attributes.id,
          type: elementName,
          children: children.map((child) => createElement(child, groupId, name)),
          styles: style,
        };
      }

      case "text":
      case "tspan": {
        return {
          ...attributes,
          id: attributes.id,
          type: elementName,
          children: children.map((child) => createElement(child, groupId, name, fontSettings)),
          textContent: textContent,
          ...parsedDataName,
        };
      }

      case "linearGradient":
      case "stop":
      case "clipPath":
      case "ellipse":
      case "circle":
      case "rect":
      case "line":
      case "polyline":
      case "polygon":
      case "defs": {
        return {
          ...attributes,
          id: attributes.id,
          type: elementName,
          children: children.map((child) => createElement(child, groupId, name, fontSettings)),
          ...parsedDataName,
        };
      }
      default:
        throw new Error(`Unknown element name ${elementName}`);
    }
  };

  const createElementList = (json: SvgJson): SmartObjectElement[] => {
    const elementList: SmartObjectElement[] = [];
    elementList.push(createElement(json));
    return elementList;
  };

  const elementList = createElementList(svgJson);

  return elementList;
}

export const svgFileToString = (file: File) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject("No file provided");
    }

    const reader = new FileReader();
    reader.onload = (e) => resolve(e?.target?.result);
    reader.onerror = () => reject("Error reading the file");
    reader.readAsText(file);
  });
};

export const parseSvgFile = async (file: File) => {
  const string = await svgFileToString(file);
  if (typeof string !== "string") {
    throw new Error("could not parsed svg file");
  }
  const parsedString = string.replace(/&amp;quot;/g, "'");
  const styles = getStylesFromSvgString(parsedString);
  const json = jsonFromSvgString(parsedString);
  const smartObject = parseSvgJsonIntoSmartObjectStructure(json, styles);

  return convertObjectIntoJsonFile(smartObject, "smartObject");
};

export const convertObjectIntoJsonFile = (data: unknown, name: string) => {
  const string = JSON.stringify(data);
  const blob = new Blob([string], { type: "application/json" });
  const file = new File([blob], `${name}.json`, { type: "application/json" });

  return file;
};

export const getStylesFromSvgString = (svg: string) => {
  // Create a temporary div element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = svg;

  // Get all <style> tags within the temporary div
  const styleTags = tempDiv.getElementsByTagName("style");
  const parsedStyles: string[] = [];

  // Loop through each <style> tag
  for (let i = 0; i < styleTags.length; i++) {
    const styleContent = styleTags[i].textContent;
    if (typeof styleContent === "string") {
      const minifiedStyle = styleContent.replace(/\n/g, "").replace(/\s+/g, " ");
      parsedStyles.push(minifiedStyle);
    }
  }

  return parsedStyles;
};

export const getVariablesFromSmartObject = (smartObject: any, objectId: string) => {
  const variables: Record<string, any> = {};
  const variablesData: Record<string, any> = {};
  const fonts: Record<string, any> = {};
  let hasHotspots = false;

  const getAttributes = (smartObject: any) => {
    const {
      TYPE,
      GROUP_ID,
      VALUE,
      ORDER,
      NAME,
      DISPLAY,
      ACTION,
      ANIMATE,
      ANIMATE_VALUE,
      HIDE_VALUE,
      RATE,
      SET_ID,
      ENABLED,
      HOTSPOT_ID,
      ANGLES,
      FONT,
      FONT_NAME,
      OPTION_STACK,
      OPTION_JUSTIFY,
      SIZE,
      CONTROL_INDEX,
      children,
    } = smartObject;

    switch (TYPE) {
      case SVG_SMART_OBJECT_TYPES.IMAGE_SWAP: {
        const key = `${objectId}-${GROUP_ID}`;

        if (variables[key] === undefined) {
          variables[key] = null;
        }

        if (variablesData[key] === undefined) {
          variablesData[key] = {
            type: SVG_SMART_OBJECT_TYPES.IMAGE_SWAP,
            name: NAME ?? GROUP_ID,
            choices: [],
            hotspots: [],
            animate: parseStringAsBoolean(ANIMATE_VALUE) ?? false,
            isAnimating: false,
            hide: parseStringAsBoolean(HIDE_VALUE) ?? false,
            rate: RATE ? Number(RATE) : 500,
            ...variablesData[key],
          };
        } else {
          /**
           * This is when a hotspot initializes the IMAGE_SWAP meta data key
           * in the variables, so we keep the values and override the ones that are falsey.
           * with the initial state
           */
          variablesData[key].name = variablesData[key].name ?? NAME ?? GROUP_ID;
          variablesData[key].choices = variablesData[key].choices ?? [];
          variablesData[key].hotspots = variablesData[key].hotspots ?? [];
          variablesData[key].animate = parseStringAsBoolean(ANIMATE_VALUE) ?? false;
          variablesData[key].hide = parseStringAsBoolean(HIDE_VALUE) ?? false;
          variablesData[key].rate = RATE ? Number(RATE) : 500;
        }

        break;
      }
      case SVG_SMART_OBJECT_TYPES.SWAP_VALUE: {
        const key = `${objectId}-${GROUP_ID}`;

        /**
         * Adding the value to the possible choices of the IMAGE_SWAP
         * Might be a good idea to add validation for duplicate
         *
         * If the meta variables is null we assign this choice as the
         * "default" value
         */
        if (variablesData[key] && VALUE) {
          variablesData[key]?.choices.push(VALUE);
          if (variables[key] === null) variables[key] = VALUE;

          if (ORDER) {
            if (!variablesData[key].order) {
              variablesData[key].order = {} as Record<string, string>;
            }

            variablesData[key].order[VALUE] = ORDER;
          }
        }
        break;
      }
      case SVG_SMART_OBJECT_TYPES.HOTSPOT: {
        const targetKey = `${objectId}-${SET_ID}`;
        const key = `${objectId}-${HOTSPOT_ID}`;
        const enabledKey = `${key}-enabled`;
        const displayKey = `${key}-display`;
        hasHotspots = true;

        variables[enabledKey] = parseStringAsBoolean(ENABLED) ?? false;
        variables[displayKey] = parseStringAsBoolean(DISPLAY) ?? false;

        const hotspotData: SmartObjectHotspot = {
          action: ACTION ?? SVG_SMART_OBJECT_HOTSPOTS_ACTIONS.SET_LITERAL,
          type: SVG_SMART_OBJECT_TYPES.HOTSPOT,
          setId: SET_ID,
          value: VALUE ?? undefined,
          target: targetKey,
          enabledKey,
          displayKey,
          id: key,
          name: NAME ?? HOTSPOT_ID,
        };

        /**
         * Depending on how the smart object is built in
         * Adobe Illustrator we could parse a hotspot
         * before parsing the IMAGE_SWAP where the hotspot belongs
         */
        if (variablesData[targetKey] === undefined) {
          variablesData[targetKey] = {
            type: SVG_SMART_OBJECT_TYPES.IMAGE_SWAP,
            name: undefined, // should be filled out when parsing the image swap
            choices: [],
            hotspots: [],
            animate: parseStringAsBoolean(ANIMATE_VALUE) ?? false,
            isAnimating: false,
            hide: parseStringAsBoolean(HIDE_VALUE) ?? false,
            rate: RATE ? Number(RATE) : 500,
            ...variablesData[targetKey],
          };
        }

        if (variablesData[enabledKey] === undefined) {
          variablesData[enabledKey] = {
            type: SVG_SMART_OBJECT_TYPES.HOTSPOT,
            target: targetKey,
            id: key,
            setId: SET_ID,
          };
        }

        if (!variablesData[targetKey]?.hotspots) {
          variablesData[targetKey].hotspots = [hotspotData];
        } else {
          variablesData[targetKey]?.hotspots.push(hotspotData);
        }

        break;
      }
      case SVG_SMART_OBJECT_TYPES.ROTATE: {
        const key = `${objectId}-${GROUP_ID}`;

        if (!ANGLES && typeof ANGLES !== "string") {
          throw `"ANGLES" property is missing for Smart Object Type ${SVG_SMART_OBJECT_TYPES.ROTATE}`;
        }

        const values: number[] = [];
        const degrees: number[] = [];

        /**
         * Changed the naming from min/max to start/end
         *  since we have to ignore the direction here there is no min and max values
         *  we should not check for their relative values
         *  this way if start is 0 and end is -100 we will "increase" until -100 without issues
         */
        let startingValue = 0;
        let endingValue = 0;
        let startingDegree = 0;
        let endingDegree = 0;

        const angles = ANGLES.split("|");

        /**
         * We have to trust that the angles will come in a linear order
         * like: 0:0|10:45|20:90|30:135 ....
         */
        angles.forEach((angle: string, index: number) => {
          const [v, d] = angle.split(":");
          const value = Number(v);
          /**
           * This negative 1 multiplication is because Adobe Illustrator
           * uses negative numbers for clockwise rotation, but it seems
           * that the property `transform=rotate` uses positive numbers instead
           */
          const degree = Number(d) * -1;

          if (Number.isNaN(value) || Number.isNaN(degree)) {
            throw "ANGLES is not formatted properly as 'value':'degree' (should only be numbers)";
          }

          // Initial Value (min)
          if (index === 0) {
            startingValue = value;
            startingDegree = degree;
          }

          // Final value (max)
          if (index === angles.length - 1) {
            endingValue = value;
            endingDegree = degree;
          }

          values.push(value);
          degrees.push(degree);
        });

        if (variables[key] === undefined) {
          // variables[key] = minValue;
          variables[key] = startingValue;
        }

        if (variablesData[key] === undefined) {
          variablesData[key] = {
            type: SVG_SMART_OBJECT_TYPES.ROTATE,
            name: NAME ?? GROUP_ID,
            minValue: startingValue,
            maxValue: endingValue,
            minDegree: startingDegree,
            maxDegree: endingDegree,
          };
        }

        break;
      }
      case SVG_SMART_OBJECT_TYPES.FILL: {
        const key = `${objectId}-${GROUP_ID}`;

        variables[key] = null;

        variablesData[key] = {
          type: SVG_SMART_OBJECT_TYPES.FILL,
          name: NAME ?? GROUP_ID,
          choices: variablesData[key]?.choices ?? [],
          colors: variablesData[key]?.colors ?? {},
        };

        break;
      }
      case SVG_SMART_OBJECT_TYPES.FILL_VALUE: {
        const key = `${objectId}-${GROUP_ID}`;
        const colors: Record<string, string> = {};
        const currentChoices = variablesData[key]?.choices ?? [];

        const choices: string[] = [];
        const pairValues = VALUE.split("|");

        for (const pair of pairValues) {
          const [value, color] = pair.split(":");

          if (currentChoices.includes(value)) {
            continue;
          }

          if (!variables[key]) {
            variables[key] = value;
          }

          choices.push(value);
          colors[value] = color;
        }

        if (choices.length > 0) {
          variablesData[key] = {
            ...variablesData[key],
            choices,
            colors,
          };
        }

        break;
      }
      case SVG_SMART_OBJECT_TYPES.DYNAMIC_TEXT: {
        const key = `${objectId}-${GROUP_ID}`;

        if (variables[key] === undefined) {
          variables[key] = null;
        }

        if (variablesData[key] === undefined) {
          // TODO: replace `undefined` for "default" value
          const font = Array.isArray(FONT)
            ? FONT.map((f) => f.replace(/['"]/g, "").trim())
            : FONT?.replace(/['"]/g, "").trim();
          const size = SIZE?.replace(/['"]/g, "").trim();

          variablesData[key] = {
            type: SVG_SMART_OBJECT_TYPES.DYNAMIC_TEXT,
            name: NAME ?? GROUP_ID,
            hide: true,
            font,
            selectedFont: Array.isArray(font) ? font[0] : font,
            size,
            CONTROL_INDEX: CONTROL_INDEX || undefined,
            ...variablesData[key],
          };
        }

        break;
      }
      case SVG_SMART_OBJECT_TYPES.IMAGE_TEXT: {
        const key = `${objectId}-${GROUP_ID}`;

        if (variables[key] === undefined) {
          variables[key] = null;
        }

        if (variablesData[key] === undefined) {
          variablesData[key] = {
            type: SVG_SMART_OBJECT_TYPES.IMAGE_TEXT,
            name: NAME ?? GROUP_ID,
            font: getSmartObjectFontKey(FONT, objectId),
            stack: OPTION_STACK ?? ".",
            justify: OPTION_JUSTIFY ?? "left",
            ...variablesData[key],
          };
        }

        break;
      }

      case SVG_SMART_OBJECT_TYPES.FONT: {
        const key = getSmartObjectFontKey(FONT_NAME, objectId);
        const characters: Record<string, any> = {};

        if (smartObject.children && smartObject.children.length > 0) {
          for (const char of smartObject.children) {
            /**
             * Sometimes illustrator renders the value with escaped double quotes
             * and sometimes with single quotes, so we replace both just in case.
             */
            if (char.FONT_CHAR) {
              const label = char.FONT_CHAR.replaceAll('"', "").replaceAll("'", "");
              characters[label] = char;
            }
          }
        }

        if (fonts[key] === undefined) {
          fonts[key] = {
            type: SVG_SMART_OBJECT_TYPES.FONT,
            name: FONT_NAME,
            characters,
            ...variablesData[key],
          };
        }

        break;
      }

      default: {
        if (TYPE) {
          console.warn("getVariablesFromSmartObject: TYPE not handled ", TYPE);
        }
        break;
      }
    }

    if (hasHotspots) {
      const keys = getHotspotFreezeTimelineKey(objectId);
      if (keys && variables[keys.key] === undefined) {
        variables[keys.key] = false;
        variablesData[keys.key] = {
          choices: [],
          type: "boolean",
          name: "Hotspots Freeze Timeline",
        };
      }
    }

    if (children) {
      children.forEach((child: any) => getAttributes(child));
    }
  };

  getAttributes(smartObject);

  return {
    variables,
    variablesData,
    fonts,
  };
};

const parseStringAsBoolean = (str: string): boolean | null => {
  if (!str || typeof str !== "string") return null;

  if (str.toLowerCase() === "true") return true;
  if (str.toLowerCase() === "false") return false;

  return true;
};

/**
 *
 * Adobe Illustrator auto generates `id` for elements inside the `defs` tag
 * this generated `ids` are usually pretty simple "image" | "image-1"
 * and there doesn't seem a way to change the id inside Adobe Illustrator.
 *
 * if multiple smart object exist in the same page, theses ids can collide, meaning the second
 * smart object rendered on the page will link to the id of a previous smart object, causing
 * the images of said smart object to be broken
 *
 * This function checks for the `ids` found inside the `def` tag and adds the `objectId`
 * of the smart object to make sure the ids are unique
 */
export const addObjectIdToDefsId = (smartObject: SmartObjectElement[], objectId: string) => {
  const newIds = new Map();

  const checkElement = (el: SmartObjectElement) => {
    if (el.type === "defs" && el.children && el.children.length > 0) {
      for (const child of el.children) {
        if (child.id) {
          const newId = `${child.id}-${objectId}`;
          newIds.set(child.id, newId);
          child.id = newId;
        }
      }
    } else if (el["xlink:href"]) {
      const idKey = el["xlink:href"].replace("#", "");
      const newId = newIds.get(idKey);
      if (newId) el["xlink:href"] = "#" + newId;
    }

    if (el.children && el.children.length > 0) {
      for (const child of el.children) {
        checkElement(child);
      }
    }
  };

  checkElement(smartObject[0]);

  return smartObject;
};

type SmartObjectVersion = {
  variables: Record<string, any>;
  variablesData: Record<string, any>;
  fonts: Record<string, any>;
};

/**
 * This function compares two smart objects versions in order to create a report
 * That will be used to determine if there is any potential data loss when replacing
 * the `oldSmartObject` with the `newSmartObject` in the asset replacement process
 *
 * The main objective is to identify a difference in either meta variables or the
 * meta variables data of the SMO, given that if the values from the `oldSmartObject`
 * cannot be found in the `newSmartObject` the data of said meta variable will be lost.
 *
 * This would mean that a developer needs to re-do all the work of each meta variable
 * that is "lost"
 *
 * Things to consider in the comparison:
 *
 * meta variables:
 *  - If a meta variable is missing in the new smart object (deleted or named differently), it should be deleted.
 *  - if a meta variable has the same name, we need to validate if the value is valid (in the meta data)
 *  - if a meta variable can be kept in the timeline
 *  - Timeline support in general***
 *
 * meta variables data:
 *  - new properties added to possible choices.
 *  - properties removed from possible choices.
 *  - values that are stored in the meta data (Ex. colors)
 *
 * fonts:
 *  - font support when upgrading might be limited***
 *  needs to be investigated/tested
 *
 *
 */
export const compareSmartObjectsVersions = (
  oldSmartObject: SmartObjectVersion, // This should come from the pageManifest
  newSmartObject: SmartObjectVersion, // this should be downloaded from blob storage
  objectId: string,
) => {
  let cleanReport = true;
  let smartObjectIsTooDifferent = false;

  const updatedMetaVariables: Record<string, string> = {};
  const updatedMetaVariablesData: Record<string, any> = {};
  const updatedAnimatedMetaVariables: AnimatedMetaVariable[] = [];

  const oldVariables = oldSmartObject.variables;
  const newVariables = newSmartObject.variables;
  const oldVariablesData = oldSmartObject.variablesData;
  const newVariablesData = newSmartObject.variablesData;

  const missingVariables = [];
  const foundVariables = [];
  const modifiedVariablesValues: Record<string, string> = {};

  /**
   * First we check for any variable that exist in the `oldSmartObject` but not in the `newSmartObject`
   * This means either a variable was deleted or renamed
   */

  for (const oldKey in oldVariables) {
    const foundKey = Object.keys(newVariables).find((newKey) => newKey === oldKey);
    if (!foundKey) {
      missingVariables.push(oldKey);
    } else {
      foundVariables.push(oldKey);
    }
  }

  const report: string[] = [];

  if (missingVariables.length === 0) {
    report.push("All meta variables (GROUP_IDs) were found in the new Smart Object");
  } else {
    cleanReport = false;
    report.push(`${missingVariables.length} missing variables found`);
    report.push(`The following meta variables (GROUP_IDs) are missing in the new Smart Object, they will be deleted`);
    missingVariables.forEach((variable) => {
      report.push(`- ${variable}`);
    });
  }

  /**
   * Now we check for all of the values that are in the meta variables that we
   * validated exist in both the `oldSmartObject` and the `newSmartObject`
   * in here we validate to see if they are "valid" values which means:
   *
   * IMAGE_SWAP: value is in the meta data choices
   * ROTATE: value is within the range of the start and end values
   * HOTSPOT: value is a valid choice in meta data choices
   * FILL: not sure if needed
   * FILL_VALUE: not sure if needed
   *
   * DYNAMIC_TEXT: no validation is needed
   * IMAGE_ TEXT: no validation is needed
   */
  for (const foundKey of foundVariables) {
    const oldVariable = oldVariables[foundKey];
    const newVariable = newVariables[foundKey];
    const oldVariableData = oldVariablesData[foundKey];
    const newVariableData = newVariablesData[foundKey];

    switch (oldVariableData.type) {
      case SVG_SMART_OBJECT_TYPES.IMAGE_SWAP: {
        if (!newVariableData.choices.includes(oldVariable)) {
          modifiedVariablesValues[foundKey] = `[IMAGE_SWAP] ${foundKey.replace(
            objectId,
            "",
          )}: value '${oldVariable}' is not in the meta data valid choices, changed to '${newVariable}'`;
          updatedMetaVariables[foundKey] = newVariable;
          cleanReport = false;
          // TODO: update possible invalid values in animated meta variables
        } else {
          updatedMetaVariables[foundKey] = oldVariable;
        }

        break;
      }
      case SVG_SMART_OBJECT_TYPES.ROTATE: {
        // TODO: validate the old variable is within the range of the new start and end values
        updatedMetaVariables[foundKey] = oldVariable;
        break;
      }
      case SVG_SMART_OBJECT_TYPES.HOTSPOT: {
        // TODO: validate hotspot value is a valid choice
        updatedMetaVariables[foundKey] = oldVariable;
        break;
      }
      case SVG_SMART_OBJECT_TYPES.FILL: {
        // TODO: implement fill validation
        break;
      }
      case SVG_SMART_OBJECT_TYPES.FILL_VALUE: {
        // TODO: implement fill value validation
        break;
      }
      case SVG_SMART_OBJECT_TYPES.DYNAMIC_TEXT:
      case SVG_SMART_OBJECT_TYPES.IMAGE_TEXT:
        updatedMetaVariables[foundKey] = oldVariable;
        // TOOD: keep animated meta variables the same.
        break;
    }
  }

  if (Object.keys(modifiedVariablesValues).length === 0) {
    report.push("All values were validated correctly");
  } else {
    cleanReport = false;
    report.push(`${Object.keys(modifiedVariablesValues).length} modified variables values found`);
    report.push(`The following variables values were modified please review them`);
    Object.keys(modifiedVariablesValues).forEach((variable) => {
      report.push(`${modifiedVariablesValues[variable]}`);
    });
  }

  if (cleanReport) {
    report.push("The Smart Object replacement will be done without any changes");
  } else {
    report.push("The Smart Object replacement wasn't validated correctly, changes will be made to the variables");
  }

  return {
    report,
    cleanReport,
    smartObjectIsTooDifferent,
  };
};
